// 안전교육 소개 페이지 — 데이터 & 진단 로직 (프론트 전용, 백엔드 무의존).
// 진단 로직은 백엔드 app/safety_education/diagnosis.py 를 TypeScript 로 1:1 재현.
// 법정 사양은 education_specs.py 정본값 인용.

// ── 별표 35 과태료 (1차 적발, 근로자 1명당) ──
const PENALTY_REGULAR = 100_000      // 정기·채용·작업변경
const PENALTY_SPECIAL = 500_000      // 특별교육
const PENALTY_CONSTRUCTION = 100_000 // 건설업 기초

export type Priority = 'urgent' | 'high' | 'medium' | 'low'

export interface DiagnosisAnswer {
  officeWorkers: number
  salesWorkers: number
  fieldWorkers: number
  supervisorCount: number
  newHires6mo: number
  jobChanges: number
  hasSpecialTask: boolean   // 별표5 라목 39종 위험작업 종사자
  hasChemical: boolean
  isConstruction: boolean
  hasForeign: boolean
}

export interface DiagnosedEducation {
  code: string
  nameKo: string
  legalBasis: string
  minimumHours: number
  targetWorkers: number
  aiEpisodes: number
  instructorHours: number
  periodicity: string
  priority: Priority
  reason: string
  penaltyKrw: number
  deadlineHint: string
  aiEligible: boolean
}

export interface DiagnosisResult {
  educations: DiagnosedEducation[]
  totalPenaltyKrw: number
  summary: string
}

const ceil = (n: number) => Math.ceil(n - 1e-9)
const aiEp = (hours: number, ratio: number) => ceil(hours * (1 - ratio))

export function diagnose(a: DiagnosisAnswer): DiagnosisResult {
  const list: DiagnosedEducation[] = []

  // ── 정기교육 ──
  if (a.officeWorkers > 0)
    list.push({
      code: 'regular_office', nameKo: '근로자 정기교육 (사무직)',
      legalBasis: '시행규칙 별표 4 제1호 가목 / 별표 5 제1호 가목',
      minimumHours: 6, targetWorkers: a.officeWorkers, aiEpisodes: aiEp(6, 0), instructorHours: 0,
      periodicity: '반기', priority: 'high',
      reason: `사무직 근로자 ${a.officeWorkers}명 — 반기 6시간 정기교육 의무 (별표 4 가목)`,
      penaltyKrw: a.officeWorkers * PENALTY_REGULAR, deadlineHint: '매 반기 종료일까지', aiEligible: true,
    })
  if (a.salesWorkers > 0)
    list.push({
      code: 'regular_sales', nameKo: '근로자 정기교육 (판매직)',
      legalBasis: '시행규칙 별표 4 제1호 가목 / 별표 5 제1호 가목',
      minimumHours: 6, targetWorkers: a.salesWorkers, aiEpisodes: aiEp(6, 0), instructorHours: 0,
      periodicity: '반기', priority: 'high',
      reason: `판매직 근로자 ${a.salesWorkers}명 — 반기 6시간 정기교육 + 고객 폭언 보호 (산안법 §41)`,
      penaltyKrw: a.salesWorkers * PENALTY_REGULAR, deadlineHint: '매 반기 종료일까지', aiEligible: true,
    })
  if (a.fieldWorkers > 0)
    list.push({
      code: 'regular_field', nameKo: '근로자 정기교육 (현장직)',
      legalBasis: '시행규칙 별표 4 제1호 가목 / 별표 5 제1호 가목',
      minimumHours: 12, targetWorkers: a.fieldWorkers, aiEpisodes: aiEp(12, 0), instructorHours: 0,
      periodicity: '반기', priority: 'urgent',
      reason: `현장직 근로자 ${a.fieldWorkers}명 — 반기 12시간 정기교육 의무 (별표 4 가목, 시간 2배)`,
      penaltyKrw: a.fieldWorkers * PENALTY_REGULAR, deadlineHint: '매 반기 종료일까지', aiEligible: true,
    })
  if (a.supervisorCount > 0)
    list.push({
      code: 'supervisor_regular', nameKo: '관리감독자 정기교육',
      legalBasis: '시행규칙 별표 4 제1호 가목 / 별표 5 제1호 나목',
      minimumHours: 16, targetWorkers: a.supervisorCount, aiEpisodes: aiEp(16, 0.5), instructorHours: 8,
      periodicity: '연간', priority: 'urgent',
      reason: `관리감독자 ${a.supervisorCount}명 — 연 16시간 + 강사 50% 의무 (안교규 §3조의2 ②3호)`,
      penaltyKrw: a.supervisorCount * PENALTY_REGULAR, deadlineHint: '매년 12월 31일까지', aiEligible: true,
    })

  // ── 채용시 ──
  if (a.newHires6mo > 0)
    list.push({
      code: 'hire_general', nameKo: '채용시 교육 (일반)',
      legalBasis: '시행규칙 별표 4 제1호 나목 / 별표 5 제1호 다목',
      minimumHours: 8, targetWorkers: a.newHires6mo, aiEpisodes: aiEp(8, 0), instructorHours: 0,
      periodicity: '1회 (신규 채용 시)', priority: 'urgent',
      reason: `최근 6개월 신규 채용 ${a.newHires6mo}명 — 직무 배치 전 8시간 채용시 교육 의무 (산안법 §29 ②항)`,
      penaltyKrw: a.newHires6mo * PENALTY_REGULAR, deadlineHint: '신규 입사 후 직무 배치 전 (즉시)', aiEligible: true,
    })

  // ── 작업변경시 ──
  if (a.jobChanges > 0) {
    const affected = Math.max(1, a.jobChanges)
    list.push({
      code: 'job_change_general', nameKo: '작업변경시 교육 (일반)',
      legalBasis: '시행규칙 별표 4 제1호 다목 / 별표 5 제1호 다목',
      minimumHours: 2, targetWorkers: affected, aiEpisodes: aiEp(2, 0), instructorHours: 0,
      periodicity: '1회 (변경 시)', priority: 'high',
      reason: `예정 작업변경 ${a.jobChanges}건 — 변경 작업 실시 전 2시간 교육 의무`,
      penaltyKrw: affected * PENALTY_REGULAR, deadlineHint: '새 작업 실시 전 (즉시)', aiEligible: true,
    })
  }

  // ── 특별교육 (별표5 라목 39종) ──
  if (a.hasSpecialTask) {
    const affected = Math.max(1, a.fieldWorkers)
    list.push({
      code: 'special_full', nameKo: '특별교육 (유해·위험작업)',
      legalBasis: '시행규칙 별표 4 제1호 라목 / 별표 5 제1호 라목',
      minimumHours: 16, targetWorkers: affected, aiEpisodes: aiEp(16, 0.667), instructorHours: 16 * 0.667,
      periodicity: '1회 (해당 작업 종사 시)', priority: 'urgent',
      reason: '별표 5 라목 유해·위험작업 종사자 — 16시간 특별교육 + 강사 67% 의무. 미실시 시 1인당 50만원 과태료',
      penaltyKrw: affected * PENALTY_SPECIAL, deadlineHint: '해당 작업 종사 전', aiEligible: true,
    })
  }

  // ── MSDS (화학물질) ──
  if (a.hasChemical) {
    const affected = Math.max(1, a.fieldWorkers + a.officeWorkers)
    list.push({
      code: 'msds', nameKo: 'MSDS(물질안전보건자료) 교육',
      legalBasis: '산안법 §114·§115 / 시행규칙 §169',
      minimumHours: 2, targetWorkers: affected, aiEpisodes: aiEp(2, 0), instructorHours: 0,
      periodicity: '취급 근로자 (변경 시 갱신)', priority: 'high',
      reason: '화학물질 취급 — 취급 근로자 대상 MSDS 교육 의무 (정기교육 내 편성 가능)',
      penaltyKrw: affected * PENALTY_REGULAR, deadlineHint: '취급 작업 배치 전', aiEligible: true,
    })
  }

  // ── 건설업 기초 (AI 불가) ──
  if (a.isConstruction)
    list.push({
      code: 'construction_basic', nameKo: '건설업 기초안전·보건교육',
      legalBasis: '시행규칙 별표 4 제1호 마목 / 별표 5 제2호',
      minimumHours: 4, targetWorkers: Math.max(1, a.fieldWorkers), aiEpisodes: 0, instructorHours: 4,
      periodicity: '1회 (건설현장 투입 전)', priority: 'urgent',
      reason: '건설업 — 일용근로자 4시간 기초안전보건교육은 집체교육 의무 (산안법 §31). 등록 교육기관 위탁 필요, AI 영상 대체 불가.',
      penaltyKrw: Math.max(1, a.fieldWorkers) * PENALTY_CONSTRUCTION, deadlineHint: '건설현장 투입 전', aiEligible: false,
    })

  const order: Record<Priority, number> = { urgent: 0, high: 1, medium: 2, low: 3 }
  list.sort((x, y) => order[x.priority] - order[y.priority] || y.penaltyKrw - x.penaltyKrw)

  const total = list.reduce((s, e) => s + e.penaltyKrw, 0)
  const summary = list.length === 0
    ? '입력하신 응답 기준 즉시 실시 필요한 법정 교육은 없습니다. 근로자 수를 입력해 보세요.'
    : `귀 사업장은 ${list.length}종 법정 교육 의무가 있습니다. 전체 미실시 시 1차 적발만으로 약 ${total.toLocaleString()}원 과태료 노출 (별표 35 기준).`

  return { educations: list, totalPenaltyKrw: total, summary }
}

// ── 법정 대체 매트릭스: 14종 교육 (별표 4·5 정본) ──
export interface EduRow {
  nameKo: string
  hours: string
  cycle: string
  instructor: string   // 강사 의무 비중
  aiEligible: boolean
}
export const EDU_14: EduRow[] = [
  { nameKo: '정기교육 (사무직)', hours: '반기 6h', cycle: '반기', instructor: '0%', aiEligible: true },
  { nameKo: '정기교육 (판매직)', hours: '반기 6h', cycle: '반기', instructor: '0%', aiEligible: true },
  { nameKo: '정기교육 (현장직)', hours: '반기 12h', cycle: '반기', instructor: '0%', aiEligible: true },
  { nameKo: '관리감독자 정기교육', hours: '연 16h', cycle: '연간', instructor: '50%', aiEligible: true },
  { nameKo: '채용시 (일용·1주이하)', hours: '1h', cycle: '채용 시', instructor: '0%', aiEligible: true },
  { nameKo: '채용시 (1주~1개월)', hours: '4h', cycle: '채용 시', instructor: '0%', aiEligible: true },
  { nameKo: '채용시 (일반)', hours: '8h', cycle: '채용 시', instructor: '0%', aiEligible: true },
  { nameKo: '작업변경시 (일용)', hours: '1h', cycle: '변경 시', instructor: '0%', aiEligible: true },
  { nameKo: '작업변경시 (일반)', hours: '2h', cycle: '변경 시', instructor: '0%', aiEligible: true },
  { nameKo: '특별교육 (일용·단기간)', hours: '2h', cycle: '작업 시', instructor: '67%', aiEligible: true },
  { nameKo: '특별교육 (타워크레인 신호)', hours: '8h', cycle: '작업 시', instructor: '67%', aiEligible: true },
  { nameKo: '특별교육 (일반 16h)', hours: '16h', cycle: '작업 시', instructor: '67%', aiEligible: true },
  { nameKo: '특수형태근로 최초 노무', hours: '2h', cycle: '최초 1회', instructor: '0%', aiEligible: true },
  { nameKo: '건설업 기초안전보건', hours: '4h', cycle: '투입 전', instructor: '집체 의무', aiEligible: false },
]

// ── 다국어 ──
export const LANGUAGES = [
  { code: 'ko', flag: '🇰🇷', label: '한국어', note: '기본 · 모든 산출물' },
  { code: 'vi', flag: '🇻🇳', label: '베트남어', note: '신규 유입 1위' },
  { code: 'zh', flag: '🇨🇳', label: '중국어(간체)', note: '조선족 산재 대응' },
  { code: 'id', flag: '🇮🇩', label: '인도네시아어', note: '제조·농업 다수' },
  { code: 'en', flag: '🇺🇸', label: '영어', note: '글로벌 인력' },
]

// ── 6종 산출물 ──
export const OUTPUTS = [
  { icon: '📊', title: 'PPTX 슬라이드', desc: '28±3장 · AI 이미지·차트·피크토그램 · 회사 로고 자동', use: '강사 미출연 강의자료' },
  { icon: '🎬', title: '강의실 영상 강의', desc: '슬라이드 + AI 음성 내레이션 동기 재생 · 43분(법정 1h=1편) · 강의실에서 바로 수강 (영상 파일 X)', use: '사이버 교육 매체' },
  { icon: '💬', title: '다국어 자막 (SRT/ASS)', desc: '언어별 분리 자막 · 점자·수어 대응(별표4 비고6)', use: '외국인·배리어프리' },
  { icon: '📝', title: '이해도 평가 퀴즈', desc: '편당 5문제 + 시리즈 평가편 · 문제지·답안지 DOCX', use: '사이버 평가(§3조의2 ②7호)' },
  { icon: '📋', title: '교육일지 PDF', desc: '안내서 79쪽 표준 양식 · 출석부·강사·시간 자동', use: '§29 보존 의무(3년)' },
  { icon: '🗂️', title: '컴플라이언스 ZIP', desc: 'PPTX·교육일지·출석부·수료증 일괄 패키지 (영상 강의는 강의실 수강)', use: '노동청 점검 일괄 제출' },
]

// ── 노동청 인정 요건 (증빙) ──
export const EVIDENCE = [
  { icon: '👤', title: '강사 감수 자동 표기', law: '안전보건교육규정 §3조의2 ②5호', desc: '강사명·자격·감수일을 PPTX 말미에 자동 슬라이드로 삽입 — 강사 미출연 영상의 적법 요건 충족.' },
  { icon: '⚖️', title: '강사 의무 비중 자동 분할', law: '안교규 §3조의2 ②3호', desc: '관리감독자 50%·특별교육 67% 강사 의무를 AI편/강사편으로 자동 계산·분리.' },
  { icon: '✅', title: '이해도 평가 강제', law: '안교규 §3조의2 ②7호', desc: '사이버 교육 필수 요건인 학습 평가를 편당·과정별 자동 생성(문제·답안 분리).' },
  { icon: '📑', title: '교육일지·출석 증빙', law: '산안법 §29 / 시행규칙 §26', desc: '표준 양식 교육일지 PDF + QR 모바일 출석으로 실시 사실·시간·대상 3년 보존.' },
]
